import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';

/**
 * Integrations Tab
 * Implements Requirement 82 AC1 / Requirement 172 AC4: integration setup.
 * Requirement 56: Ticketing Adapters - Jira and ServiceNow are both real
 * ticketing adapters (ticketing-adapter.ts's ported SPI); Freshservice is
 * honestly shown as not implemented rather than faked as connected.
 * Requirement 57: Notification Fabric - Teams is wired as a real
 * per-tenant notification channel (see notification.service.ts's
 * notifyEvent, fired on anomaly/budget/recommendation/policy events), with
 * automatic email fallback - not the full multi-channel/rate-limited fabric
 * the spec describes, a deliberately simpler version for this platform's
 * scale.
 */

type IntegrationType = 'jira' | 'teams' | 'servicenow';

interface IntegrationStatus {
  id: string;
  name: string;
  type: string;
  status: 'connected' | 'error' | 'pending' | 'not_implemented';
  implemented: boolean;
  description: string;
  hasStoredSecret?: boolean;
  lastVerifiedAt?: string | null;
  verificationError?: string | null;
}

interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'password';
  required?: boolean;
}

const FIELD_DEFS: Record<IntegrationType, FieldDef[]> = {
  jira: [
    { key: 'baseUrl', label: 'Jira Base URL', placeholder: 'https://yourcompany.atlassian.net', required: true },
    { key: 'email', label: 'Jira Account Email', placeholder: 'you@yourcompany.com', required: true },
    { key: 'apiToken', label: 'Jira API Token', placeholder: 'stored in Key Vault - never shown again after saving', type: 'password', required: true },
    { key: 'projectKey', label: 'Jira Project Key', placeholder: 'FINOPS', required: true },
    { key: 'requestTypeName', label: 'Service Desk Request Type (optional - only for Jira Service Management projects)', placeholder: 'e.g., Finops Platform Request - leave blank for a regular Jira project' },
  ],
  teams: [
    { key: 'webhookUrl', label: 'Teams Incoming Webhook URL', placeholder: 'stored in Key Vault - never shown again after saving', type: 'password', required: true },
  ],
  servicenow: [
    { key: 'baseUrl', label: 'ServiceNow Instance URL', placeholder: 'https://yourinstance.service-now.com', required: true },
    { key: 'username', label: 'ServiceNow Username', placeholder: 'a scoped integration user, not a personal account', required: true },
    { key: 'password', label: 'ServiceNow Password', placeholder: 'stored in Key Vault - never shown again after saving', type: 'password', required: true },
  ],
};

const WIZARD_COPY: Record<IntegrationType, string> = {
  jira: 'Connect a real Jira project so "Raise Ticket" on Recommendations/Anomalies creates real tickets here.',
  teams: 'Connect a Teams channel via an Incoming Webhook - used for real-time notifications on new anomalies, budget alerts, recommendations, and policy violations.',
  servicenow: 'Connect a real ServiceNow instance so "Raise Ticket" on Recommendations/Anomalies creates real incidents here instead of Jira.',
};

export const IntegrationsTab: React.FC = () => {
  const {
    data: integrations,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => adminService.getIntegrations(),
  });

  const [testResult, setTestResult] = React.useState<{ id: string; success: boolean; message: string } | null>(null);
  const [testing, setTesting] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const [showWizard, setShowWizard] = useState(false);
  const [selectedType, setSelectedType] = useState<IntegrationType | null>(null);
  const [name, setName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const getIntegrationIcon = (type: string) => {
    const icons: Record<string, string> = {
      jira: '📋',
      teams: '👥',
      servicenow: '🎫',
      pagerduty: '📟',
      freshservice: '🔧',
    };
    return icons[type] || '🔌';
  };

  const getStatusBadge = (status: IntegrationStatus['status']) => {
    const styles: Record<string, string> = {
      connected: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-200',
      error: 'bg-rose-500/10 text-rose-600 dark:bg-rose-900 dark:text-rose-200',
      pending: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900 dark:text-amber-200',
      not_implemented: 'bg-secondary text-muted-foreground dark:bg-secondary dark:text-muted-foreground/70',
    };
    const labels: Record<string, string> = {
      connected: 'CONNECTED',
      error: 'CONNECTION FAILED',
      pending: 'PENDING VERIFICATION',
      not_implemented: 'NOT IMPLEMENTED',
    };

    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${styles[status] || styles.not_implemented}`}>
        {labels[status] || status.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const handleTestConnection = async (integrationId: string) => {
    setTesting(integrationId);
    setTestResult(null);
    try {
      const result = await adminService.testIntegration(integrationId);
      setTestResult({ id: integrationId, ...result });
      if (result.success) refetch();
    } catch (error: any) {
      setTestResult({ id: integrationId, success: false, message: error.response?.data?.message || 'Failed to test connection' });
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await adminService.deleteIntegration(id);
      refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to remove integration');
    } finally {
      setDeletingId(null);
    }
  };

  const resetWizard = () => {
    setShowWizard(false);
    setSelectedType(null);
    setName('');
    setFieldValues({});
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!selectedType) return;
    const missing = FIELD_DEFS[selectedType].filter((f) => f.required && !fieldValues[f.key]?.trim());
    if (!name.trim() || missing.length > 0) {
      setSubmitError(
        !name.trim() ? 'Name is required' : `Missing required field(s): ${missing.map((f) => f.label).join(', ')}`
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await adminService.createIntegration(selectedType, name.trim(), fieldValues);
      refetch();
      resetWizard();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to add integration');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading integrations..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load integrations"
        message="Unable to fetch integration data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Integrations</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Jira, ServiceNow (Requirement 56), and Teams (Requirement 57) are real, per-tenant integrations -
            add your own below. PagerDuty and Freshservice are shown honestly as not implemented, not
            faked as connected.
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Integration
        </button>
      </div>

      {testResult && (
        <div className={`rounded-md px-4 py-3 text-sm ${testResult.success ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' : 'bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:text-rose-200'}`}>
          {testResult.message}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {(integrations || []).map((integration: IntegrationStatus) => (
          <div
            key={integration.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getIntegrationIcon(integration.type)}</span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{integration.name}</h3>
                  <p className="text-sm text-muted-foreground">{integration.description}</p>
                </div>
              </div>
              {getStatusBadge(integration.status)}
            </div>

            {integration.status === 'not_implemented' && (
              <div className="mt-4 rounded-md bg-muted/30 p-3 dark:bg-card/40">
                <p className="text-sm text-muted-foreground">Not built yet - shown honestly, not faked as connected.</p>
              </div>
            )}

            {integration.verificationError && (
              <div className="mt-4 rounded-md bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                {integration.verificationError}
              </div>
            )}

            {integration.hasStoredSecret && (
              <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secret stored in Key Vault
              </div>
            )}

            {integration.implemented && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleTestConnection(integration.id)}
                  disabled={testing === integration.id}
                  className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                >
                  {testing === integration.id ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  onClick={() => handleDelete(integration.id)}
                  disabled={deletingId === integration.id}
                  title="Remove integration"
                  className="rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-border dark:bg-secondary dark:hover:bg-secondary"
                >
                  {deletingId === integration.id ? '…' : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">Add Integration</h3>
              <button onClick={resetWizard} className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-6">
              {!selectedType ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Select an integration to add:</p>
                  <div className="grid gap-4 md:grid-cols-3">
                    {(['jira', 'servicenow', 'teams'] as IntegrationType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className="flex flex-col items-center gap-3 rounded-lg border-2 border-border p-4 hover:border-primary/30 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:hover:border-primary/50 dark:hover:bg-secondary"
                      >
                        <span className="text-4xl">{getIntegrationIcon(type)}</span>
                        <span className="font-medium text-foreground">{type === 'jira' ? 'Jira' : type === 'servicenow' ? 'ServiceNow' : 'Microsoft Teams'}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg bg-primary/10 p-4 dark:bg-primary/10">
                    <h4 className="text-sm font-medium text-primary">
                      {selectedType === 'jira' ? 'Jira' : selectedType === 'servicenow' ? 'ServiceNow' : 'Microsoft Teams'} Setup
                    </h4>
                    <p className="mt-2 text-sm text-primary">{WIZARD_COPY[selectedType]}</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground/80">Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={selectedType === 'jira' ? 'e.g., Production Jira' : selectedType === 'servicenow' ? 'e.g., Production ServiceNow' : 'e.g., Platform Team Channel'}
                        className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                      />
                    </div>

                    {FIELD_DEFS[selectedType].map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-foreground/80">{field.label}</label>
                        <input
                          type={field.type || 'text'}
                          value={fieldValues[field.key] || ''}
                          onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                          className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                        />
                      </div>
                    ))}
                  </div>

                  {submitError && (
                    <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                      {submitError}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 border-t border-border pt-4 dark:border-border">
                    <button
                      onClick={() => setSelectedType(null)}
                      disabled={submitting}
                      className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      {submitting ? 'Saving…' : 'Add Integration'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
